import type { StaffAnalyticsMetric } from "@lafayette/schemas";
import { type PointerEvent, useRef, useState } from "react";

import { Button } from "../../ui/button";
import { niceMax } from "../charts/chart-math";
import { dateText } from "../format";
import {
  bucketAt,
  editWindow,
  type WindowEdit,
  windowIndices,
  type Zoom,
  zoomOfBuckets,
} from "./zoom-math";

// The navigator strip under the chart: the WHOLE range as small bars, with a window that shows what the chart shows. A person with a mouse or a
// finger drags the window to move it and drags its edges to resize it; the window snaps to the buckets of the strip. A person with a keyboard
// uses the buttons under the strip (earlier, later, narrower, wider). Either way the window is a zoom, kept in the address.

const HEIGHT = 44;
const HANDLE = 8;
const FALLBACK_WIDTH = 640;

type Drag = Readonly<{
  mode: "move" | "start" | "end";
  origin: number;
  from: readonly [number, number];
}>;

const BUTTONS: readonly Readonly<{ edit: WindowEdit; label: string }>[] = [
  { edit: "earlier", label: "Earlier" },
  { edit: "later", label: "Later" },
  { edit: "narrower", label: "Narrower" },
  { edit: "wider", label: "Wider" },
];

function dragged(drag: Drag, index: number, count: number): readonly [number, number] {
  const [first, last] = drag.from;
  if (drag.mode === "start") return [Math.min(index, last), last];
  if (drag.mode === "end") return [first, Math.max(index, first)];
  const shift = Math.min(Math.max(index - drag.origin, -first), count - 1 - last);
  return [first + shift, last + shift];
}

export function ExploreNavigator({
  metric,
  zoom,
  tz,
  onZoom,
  onClear,
}: Readonly<{
  /** The whole range, not split and not zoomed. */
  metric: StaffAnalyticsMetric;
  zoom: Zoom | null;
  tz: string;
  onZoom: (zoom: Zoom, mode: "push" | "replace") => void;
  onClear: () => void;
}>) {
  const points = metric.series[0]?.points ?? [];
  const starts = points.map((point) => point.t);
  const bound: Zoom = { from: metric.access.window.from, to: metric.access.window.to };
  const committed = windowIndices(starts, zoom);
  const [draft, setDraft] = useState<readonly [number, number] | null>(null);
  const drag = useRef<Drag | null>(null);
  const frame = useRef<HTMLDivElement | null>(null);
  const count = points.length;
  const width = frame.current?.clientWidth || FALLBACK_WIDTH;
  const slot = width / Math.max(count, 1);
  const max = niceMax(Math.max(0, ...points.map((point) => point.value)));
  const shown = draft ?? committed;
  const whole = shown[0] === 0 && shown[1] === count - 1;

  const indexOf = (event: PointerEvent<Element>) => {
    const box = frame.current?.getBoundingClientRect();
    return bucketAt(event.clientX - (box?.left ?? 0), box?.width ?? width, count);
  };
  const begin = (mode: Drag["mode"]) => (event: PointerEvent<SVGElement>) => {
    event.stopPropagation();
    drag.current = { mode, origin: indexOf(event), from: committed };
    event.currentTarget.setPointerCapture?.(event.pointerId);
  };
  const move = (event: PointerEvent<SVGSVGElement>) => {
    if (drag.current !== null) setDraft(dragged(drag.current, indexOf(event), count));
  };
  const commit = (window: readonly [number, number], mode: "push" | "replace") => {
    const next = zoomOfBuckets(starts, window[0], window[1], bound);
    if (next !== null) onZoom(next, mode);
  };
  const end = () => {
    const pending = draft;
    drag.current = null;
    setDraft(null);
    if (pending === null) return;
    if (pending[0] === 0 && pending[1] === count - 1) onClear();
    else commit(pending, zoom === null ? "push" : "replace");
  };
  const press = (edit: WindowEdit) => {
    const next = editWindow(edit, committed, count);
    if (next[0] === 0 && next[1] === count - 1) onClear();
    else commit(next, zoom === null ? "push" : "replace");
  };

  if (count === 0) return null;
  const x = shown[0] * slot;
  const span = (shown[1] - shown[0] + 1) * slot;
  return (
    <section
      aria-labelledby="explore-navigator"
      className="grid gap-2 border-t border-hairline pt-3"
    >
      <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1 text-xs text-muted-foreground">
        <h3 id="explore-navigator" className="m-0 text-xs font-semibold text-foreground">
          The whole range
        </h3>
        <span>
          {dateText(bound.from, tz)} → {dateText(bound.to, tz)}
        </span>
      </div>
      <div ref={frame} className="relative">
        <svg
          width="100%"
          height={HEIGHT}
          viewBox={`0 0 ${width} ${HEIGHT}`}
          preserveAspectRatio="none"
          aria-hidden="true"
          focusable="false"
          className="block touch-none rounded-md bg-surface-sunken"
          onPointerMove={move}
          onPointerUp={end}
          onPointerCancel={() => {
            drag.current = null;
            setDraft(null);
          }}
        >
          {points.map((point, index) => {
            const height = Math.max(1, (point.value / max) * (HEIGHT - 6));
            return (
              <rect
                key={point.t}
                x={index * slot + slot * 0.15}
                y={HEIGHT - height - 2}
                width={Math.max(1, slot * 0.7)}
                height={height}
                className="fill-info"
              />
            );
          })}
          <rect
            x={x}
            y={1}
            width={span}
            height={HEIGHT - 2}
            rx={8}
            className="cursor-grab fill-accent stroke-primary"
            fillOpacity={0.35}
            strokeWidth={2}
            vectorEffect="non-scaling-stroke"
            onPointerDown={begin("move")}
          />
          <rect
            x={x}
            y={1}
            width={HANDLE}
            height={HEIGHT - 2}
            className="cursor-ew-resize fill-primary"
            onPointerDown={begin("start")}
          />
          <rect
            x={x + span - HANDLE}
            y={1}
            width={HANDLE}
            height={HEIGHT - 2}
            className="cursor-ew-resize fill-primary"
            onPointerDown={begin("end")}
          />
        </svg>
      </div>
      {/* biome-ignore lint/a11y/useSemanticElements: a row of buttons, not a form group: a fieldset would add a border and a legend */}
      <div
        role="group"
        aria-label="Move or resize the window"
        className="flex flex-wrap items-center gap-2"
      >
        {BUTTONS.map((button) => (
          <Button
            key={button.edit}
            type="button"
            variant="outline"
            size="sm"
            onClick={() => press(button.edit)}
          >
            {button.label}
          </Button>
        ))}
        <Button
          type="button"
          variant="ghost"
          size="sm"
          disabled={whole && zoom === null}
          onClick={onClear}
        >
          Show the whole range
        </Button>
      </div>
    </section>
  );
}
